import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ApiConfig } from '@/config/api.config';
import { UrlHelper } from '@/utils/UrlHelper';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';
import { useSuperAdminOwnersFilter } from '@/features/superAdmin/features/data/owners/hooks/useSuperAdminOwnersFilter';
import { SuperAdminOwnerListResponseDto } from '@/features/superAdmin/features/data/types/superAdmin-data.types';

type Props = {
    suspendFetching?: boolean;
};

export const useSuperAdminOwnersListApi = ({ suspendFetching }: Props = {}) => {
    const axiosInstance = useAxiosAuth();
    const { filter } = useSuperAdminOwnersFilter();
    const { take, skip, tenantId, searchText, orderBy, orderDirection } = filter;

    const { data, isLoading, error } = useQuery({
        queryKey: [
            ApiConfig.queries.superAdmin.data.owners.queryKeys.list,
            take,
            skip,
            tenantId,
            searchText,
            orderBy,
            orderDirection,
        ],
        queryFn: async (): Promise<SuperAdminOwnerListResponseDto> => {
            const url = UrlHelper.buildUrlWithQueryParams(
                ApiConfig.queries.superAdmin.data.owners.endpoints.list,
                {
                    skip,
                    take,
                    tenantId: tenantId ?? undefined,
                    searchText: searchText ?? undefined,
                    orderBy: orderBy ?? undefined,
                    orderDirection,
                },
            );
            const response = await axiosInstance.get<SuperAdminOwnerListResponseDto>(url);
            return response.data;
        },
        enabled: !suspendFetching,
        placeholderData: keepPreviousData,
    });

    return {
        list: data,
        isLoading,
        error,
    };
};
